import React, { useEffect, useState } from 'react';
import { CButton, CCard, CCardBody, CCol, CProgress, CRow, CSpinner } from '@coreui/react-pro';
import CIcon from '@coreui/icons-react';
import { cilBullhorn, cilPencil, cilPlus } from '@coreui/icons';
import { useNavigate } from 'react-router-dom';
import * as DomicilappService from '../../services/DomicilappService';
import { useStore } from '../../contexts/StoreContext';
import { useNotification } from '../../contexts/NotificationContext';
import { AdsPricing, SERVICE_BANNER, SERVICE_FLASH, SERVICE_PUSH, StoreAd } from '../../types/marketing';
import { StatusBadge, formatCOP, formatDate } from './marketingUtils';
import AdEditModal from './AdEditModal';

const CHANNEL_NAMES: Record<number, string> = { [SERVICE_PUSH]: 'Push', [SERVICE_BANNER]: 'Banner', [SERVICE_FLASH]: 'Flash message' };
const CONTENT_NAMES: Record<number, string> = { 2: 'Promoción', 3: 'Evento', 4: 'Oferta', 5: 'Cupón' };
const AUDIENCE_NAMES: Record<string, string> = { municipality: 'tu municipio', followers: 'tus seguidores', customers: 'tus clientes' };

const AdsList: React.FC = () => {
    const navigate = useNavigate();
    const { storeId } = useStore();
    const { showToast } = useNotification();
    const [ads, setAds] = useState<StoreAd[]>([]);
    const [pricing, setPricing] = useState<AdsPricing | null>(null);
    const [loading, setLoading] = useState(false);
    const [finishingId, setFinishingId] = useState<number | null>(null);
    const [editingAd, setEditingAd] = useState<StoreAd | null>(null);

    const load = () => {
        if (!storeId) return;
        setLoading(true);
        Promise.all([DomicilappService.getStoreAds(storeId), DomicilappService.getAdsPricing(storeId)])
            .then(([adsResponse, pricingResponse]) => {
                setAds(adsResponse?.status === 200 ? adsResponse.data?.items ?? [] : []);
                setPricing(pricingResponse?.status === 200 ? pricingResponse.data : null);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        setAds([]);
        setPricing(null);
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeId]);

    const handleFinish = (ad: StoreAd) => {
        setFinishingId(ad.id);
        DomicilappService.finishStoreAd(storeId, ad.id)
            .then((response: any) => {
                if (response?.status === 200) {
                    showToast({ title: 'Anuncio terminado', message: 'Lo que no se consumió volvió a tu saldo', type: 'success', delay: 3500 });
                    load();
                } else {
                    showToast({ title: '¡Ha ocurrido un error!', message: response?.message, type: 'danger', delay: 3500 });
                }
            })
            .finally(() => setFinishingId(null));
    };

    return (
        <div className="ads-page">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="fw-bold text-body mb-1">Publicidad</h3>
                    <p className="text-body-secondary small mb-0">Da a conocer tus ofertas, promociones, cupones y eventos dentro de la app</p>
                </div>
                <CButton color="dark" className="d-flex align-items-center gap-2" onClick={() => navigate('/marketing/ads/create')}>
                    <CIcon icon={cilPlus} size="sm" />
                    <span>Nueva campaña</span>
                </CButton>
            </div>

            <CRow className="g-3 mb-4">
                <CCol sm={6} lg={4}>
                    <CCard className="border-0 shadow-sm rounded-3">
                        <CCardBody className="p-3">
                            <span className="text-uppercase text-body-secondary fw-bold" style={{ fontSize: '0.7rem' }}>Saldo disponible</span>
                            <h4 className="fw-bold text-body mb-0 mt-1">{formatCOP(pricing?.balance ?? 0)}</h4>
                        </CCardBody>
                    </CCard>
                </CCol>
                <CCol sm={6} lg={4}>
                    <CCard className="border-0 shadow-sm rounded-3">
                        <CCardBody className="p-3">
                            <span className="text-uppercase text-body-secondary fw-bold" style={{ fontSize: '0.7rem' }}>Tu plan</span>
                            <h4 className="fw-bold text-body mb-0 mt-1 text-capitalize">{pricing?.plan_name ?? '—'}</h4>
                            {pricing && pricing.prices.length === 0 && <small className="text-warning">Tu plan no incluye publicidad</small>}
                        </CCardBody>
                    </CCard>
                </CCol>
            </CRow>

            {loading ? (
                <div className="text-center py-5"><CSpinner size="sm" className="me-2" /><span className="text-body-secondary">Cargando campañas...</span></div>
            ) : ads.length === 0 ? (
                <CCard className="border-0 shadow-sm rounded-3">
                    <CCardBody className="text-center py-5">
                        <CIcon icon={cilBullhorn} size="xl" className="text-body-secondary mb-2" />
                        <p className="text-body-secondary mb-0">Aún no tienes campañas. Crea un descuento y publicítalo con banner, flash message o notificación push.</p>
                    </CCardBody>
                </CCard>
            ) : (
                <CRow className="g-3">
                    {ads.map((ad) => {
                        const push = ad.channels.find((c) => c.service_id === SERVICE_PUSH);
                        const display = ad.channels.find((c) => c.service_id !== SERVICE_PUSH);
                        const ctr = ad.impressions > 0 ? Math.round((ad.clicks / ad.impressions) * 1000) / 10 : 0;
                        return (
                            <CCol md={6} xl={4} key={ad.id}>
                                <CCard className="border-0 shadow-sm rounded-3 h-100">
                                    {ad.image_url && <img src={ad.image_url} alt="" className="rounded-top-3" style={{ height: '140px', objectFit: 'cover' }} />}
                                    <CCardBody className="p-3 d-flex flex-column">
                                        <div className="d-flex justify-content-between align-items-start mb-1">
                                            <small className="text-body-secondary">{CONTENT_NAMES[ad.service_types_id] ?? 'Anuncio'} · #{ad.id}</small>
                                            <StatusBadge status={ad.status} />
                                        </div>
                                        <h6 className="fw-bold text-body mb-1">{ad.title}</h6>
                                        <small className="text-body-secondary mb-2">{formatDate(ad.start_date)} → {formatDate(ad.end_date)}</small>

                                        {display && ad.budget !== null && (
                                            <div className="mb-2">
                                                <div className="d-flex justify-content-between small">
                                                    <span className="text-body">{CHANNEL_NAMES[display.service_id]}</span>
                                                    <span className="text-body-secondary">{formatCOP(ad.spent)} de {formatCOP(ad.budget)}</span>
                                                </div>
                                                <CProgress thin value={ad.budget > 0 ? Math.min(100, (ad.spent / ad.budget) * 100) : 0} className="my-1" />
                                                <small className="text-body-secondary">{ad.impressions} impresiones · {ad.clicks} clicks · {ctr}% CTR</small>
                                            </div>
                                        )}
                                        {push && (
                                            <div className="small mb-2">
                                                <span className="text-body">Push</span>{' '}
                                                <span className="text-body-secondary">
                                                    {push.sent_at
                                                        ? `enviada a ${push.sent_count ?? 0} de ${push.recipients_count} (${AUDIENCE_NAMES[ad.audience ?? 'municipality']})`
                                                        : `programada para ${push.recipients_count} clientes · ${push.schedule_date ? formatDate(push.schedule_date) : 'al iniciar'}`}
                                                </span>
                                            </div>
                                        )}

                                        {ad.status !== 'finished' && (
                                            <div className="d-flex gap-2 mt-auto">
                                                <CButton color="light" size="sm" onClick={() => setEditingAd(ad)}>
                                                    <CIcon icon={cilPencil} size="sm" className="me-1" /> Editar
                                                </CButton>
                                                <CButton color="light" size="sm" className="flex-grow-1" disabled={finishingId === ad.id} onClick={() => handleFinish(ad)}>
                                                    {finishingId === ad.id && <CSpinner size="sm" className="me-1" />}
                                                    Terminar y devolver saldo no usado
                                                </CButton>
                                            </div>
                                        )}
                                    </CCardBody>
                                </CCard>
                            </CCol>
                        );
                    })}
                </CRow>
            )}

            <AdEditModal
                storeId={storeId}
                ad={editingAd}
                balance={pricing?.balance ?? 0}
                onClose={() => setEditingAd(null)}
                onSaved={() => {
                    setEditingAd(null);
                    load();
                }}
            />
        </div>
    );
};

export default AdsList;
